/**
 * The minigames' comic look (owner, Sep 28: minigames "in the game's comic style"): flat colour under a heavy black line, halftone
 * dots for shade, starbursts with shouted words. Small helpers every game draws with, in its own logical pixels.
 */
import { POP_S, type Core } from './core';

export const INK = '#15121a';
export const FONT = '"Bangers", "Impact", "Arial Black", system-ui, sans-serif';

/** a filled shape outlined in ink */
export function ink(ctx: CanvasRenderingContext2D, fill: string, lw = 1.5): void { ctx.fillStyle = fill; ctx.fill(); ctx.lineWidth = lw; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.stroke(); }
/** a rounded box, filled and inked */
export function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string, r = 2, lw = 1.5): void { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); ink(ctx, fill, lw); }
/** a circle, filled and inked */
export function disc(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string, lw = 1.5): void { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ink(ctx, fill, lw); }
/** halftone dots over a rectangle (the comic's shading) */
export function halftone(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string, step = 6, r = 1): void {
  ctx.fillStyle = color;
  for (let j = 0, yy = y; yy < y + h; j++, yy += step) for (let xx = x + (j % 2 ? step / 2 : 0); xx < x + w; xx += step) { ctx.beginPath(); ctx.arc(xx, yy, r, 0, Math.PI * 2); ctx.fill(); }
}
/** a starburst (the POW! balloon) */
export function burst(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string, spikes = 10): void {
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) { const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2, rr = i % 2 ? r * 0.62 : r; ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
  ctx.closePath(); ink(ctx, fill, 1.5);
}
/** shouted words: white (or `fill`) with a thick ink edge */
export function shout(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, fill = '#fff', align: CanvasTextAlign = 'center'): void {
  ctx.font = `${size}px ${FONT}`; ctx.textAlign = align; ctx.textBaseline = 'middle';
  ctx.lineWidth = Math.max(2, size / 5); ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.strokeText(text, x, y);
  ctx.fillStyle = fill; ctx.fillText(text, x, y);
}
/** the bursts over the play: they spring up, hang, fade */
export function pops(ctx: CanvasRenderingContext2D, s: Core): void {
  for (const p of s.pops) {
    const k = p.t / POP_S, sc = k < 0.15 ? 0.5 + (k / 0.15) * 0.6 : 1.1 - Math.min(0.1, (k - 0.15) * 0.3);
    ctx.save(); ctx.globalAlpha = k > 0.7 ? Math.max(0, (1 - k) / 0.3) : 1; ctx.translate(p.x, p.y - k * 10); ctx.scale(sc, sc);
    const big = p.text.length <= 5;
    if (big) burst(ctx, 0, 0, 7 + p.text.length * 3.2, p.color);
    shout(ctx, p.text, 0, 0.5, big ? 11 : 9, big ? '#fff' : p.color);
    ctx.restore();
  }
}
/** the score and the hearts along the top */
export function hud(ctx: CanvasRenderingContext2D, s: Core, w: number, extra?: string): void {
  shout(ctx, `${s.score}`, w - 6, 11, 14, '#ffe14a', 'right');
  if (s.lives !== undefined) for (let i = 0; i < s.lives; i++) heart(ctx, 10 + i * 13, 11, 5);
  if (extra) shout(ctx, extra, w / 2, 11, 10, '#fff');
}
export function heart(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath(); ctx.moveTo(x, y + r * 0.9);
  ctx.bezierCurveTo(x - r * 1.6, y - r * 0.2, x - r * 0.7, y - r * 1.4, x, y - r * 0.4);
  ctx.bezierCurveTo(x + r * 0.7, y - r * 1.4, x + r * 1.6, y - r * 0.2, x, y + r * 0.9);
  ink(ctx, '#ff3b4a', 1.2);
}
/** a car from above (the racer, the avenue, the courier's traffic): body, glass, wheels — `len` along its heading, which is +x when `a` is 0 */
export function carTop(ctx: CanvasRenderingContext2D, x: number, y: number, len: number, wid: number, a: number, color: string): void {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.fillStyle = INK; for (const [wx, wy] of [[-0.3, -0.5], [0.3, -0.5], [-0.3, 0.5], [0.3, 0.5]]) ctx.fillRect(wx * len - 2.5, wy * wid - 1.5, 5, 3);
  box(ctx, -len / 2, -wid / 2, len, wid, color, 3);
  ctx.fillStyle = 'rgba(190,230,255,0.9)'; ctx.fillRect(len * 0.08, -wid / 2 + 2, len * 0.16, wid - 4); ctx.fillRect(-len * 0.34, -wid / 2 + 2.5, len * 0.12, wid - 5);
  ctx.fillStyle = 'rgba(255,255,255,0.35)'; ctx.fillRect(-len / 2 + 2, -wid / 2 + 1.5, len - 4, 1.5);
  ctx.restore();
}
